import React, { useEffect, useState } from 'react';
import { useAuth } from './context/AuthContext';

function readAuthRedirectError() {
  if (typeof window === 'undefined') return '';
  const raw = window.location.hash?.replace(/^#/, '') || '';
  if (!raw) return '';
  const params = new URLSearchParams(raw);
  const code = params.get('error_code') || '';
  const description = (params.get('error_description') || '')
    .replace(/\+/g, ' ')
    .trim();
  if (!params.get('error') && !code && !description) return '';

  if (code === 'otp_expired' || /invalid or has expired/i.test(description)) {
    return 'This invite or confirmation link is invalid or has expired. Ask an admin to resend the invite, then open the newest email once (do not forward or preview-click the link).';
  }
  if (params.get('error') === 'access_denied') {
    return description || 'Access was denied for this email link. Request a new invite from your admin.';
  }
  return description || 'Could not complete email confirmation.';
}

export default function Login() {
  const { signIn, signUp, signInWithGoogle, requestPasswordReset } = useAuth();
  const [mode, setMode] = useState('login'); // login | signup | forgot
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const redirectError = readAuthRedirectError();
    if (!redirectError) return;
    setError(redirectError);
    // Clear hash so a refresh does not keep showing the same error.
    window.history.replaceState(
      null,
      document.title,
      `${window.location.pathname}${window.location.search}`
    );
  }, []);

  const formatClientAuthError = (err, fallback) => {
    if (err == null) return fallback;
    if (typeof err === 'string') {
      const trimmed = err.trim();
      return !trimmed || trimmed === '{}' ? fallback : trimmed;
    }
    const candidates = [
      err.message,
      err.error_description,
      err.error,
      err.msg,
      err.details,
    ];
    for (const value of candidates) {
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed && trimmed !== '{}' && trimmed !== '[object Object]') {
          return trimmed;
        }
      }
    }
    const status = err.status || err.statusCode;
    if (status) {
      return `${fallback} (code ${status}). Check Supabase Auth → Emails → SMTP Settings.`;
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setInfo('');
    setSubmitting(true);

    try {
      if (mode === 'forgot') {
        await requestPasswordReset(email.trim());
        setInfo('Password reset email sent. Check your inbox and spam folder, then follow the link.');
      } else if (mode === 'login') {
        await signIn(email.trim(), password);
      } else {
        await signUp(email.trim(), password, fullName.trim());
        setInfo(
          'Account created. If email confirmation is enabled in Supabase, check your inbox before logging in.'
        );
        setMode('login');
      }
    } catch (err) {
      setError(
        formatClientAuthError(
          err,
          mode === 'forgot'
            ? 'Could not send the reset email. In Supabase, set SMTP Sender email to the same address as Username (e.g. both dev@tikix.io), save, then try again.'
            : 'Authentication failed'
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setInfo('');
    setSubmitting(true);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(formatClientAuthError(err, 'Google sign-in failed'));
      setSubmitting(false);
    }
  };

  const changeMode = (nextMode) => {
    setMode(nextMode);
    setError('');
    setInfo('');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-sm p-8">
        <div className="flex items-center space-x-4 mb-6">
          <img
            src="/tikix-logo.png"
            alt="Tikix"
            className="w-28 h-auto"
          />
          <div>
            <h1 className="text-lg font-black text-purple-900 tracking-tight">PMS Tracker</h1>
            <p className="text-xs text-gray-500 font-medium">
              {mode === 'login'
                ? 'Sign in to your project'
                : mode === 'signup'
                  ? 'Create an employee account'
                  : 'Reset your password'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
              {mode === 'signup' && (
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1">Full name</label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                  placeholder="Jane Doe"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  New accounts are created as Employee. Promote admins in Supabase (profiles table).
                </p>
              </div>
              )}

          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1">Email</label>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
              placeholder="you@company.com"
            />
          </div>

          {mode !== 'forgot' && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-gray-500">Password</label>
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => changeMode('forgot')}
                    className="text-xs text-purple-700 font-bold hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <input
                type="password"
                required
                minLength={6}
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 focus:outline-none"
                placeholder="At least 6 characters"
              />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-xs font-medium rounded-xl px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="bg-green-50 border border-green-200 text-green-800 text-xs font-medium rounded-xl px-3 py-2">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-purple-600 text-white px-5 py-3 rounded-xl text-sm font-bold hover:bg-purple-700 shadow-sm disabled:opacity-60"
          >
            {submitting
              ? 'Please wait…'
              : mode === 'login'
                ? 'Sign in'
                : mode === 'signup'
                  ? 'Create account'
                  : 'Send reset link'}
          </button>
        </form>

        {mode !== 'forgot' && (
          <>
            <div className="flex items-center gap-3 my-5">
              <div className="h-px flex-1 bg-gray-200" />
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                or
              </span>
              <div className="h-px flex-1 bg-gray-200" />
            </div>
            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={submitting}
              className="w-full border border-gray-300 bg-white text-gray-700 px-5 py-3 rounded-xl text-sm font-bold hover:bg-gray-50 disabled:opacity-60 flex items-center justify-center gap-3"
            >
              <span className="text-lg font-black text-blue-600">G</span>
              Continue with Google
            </button>
          </>
        )}

        <p className="text-center text-xs text-gray-500 mt-5">
          {mode === 'login' ? (
            <>
              New employee?{' '}
              <button
                type="button"
                onClick={() => changeMode('signup')}
                className="text-purple-700 font-bold hover:underline"
              >
                Create an account
              </button>
            </>
          ) : mode === 'signup' ? (
            <>
              Already have an account?{' '}
              <button
                type="button"
                onClick={() => changeMode('login')}
                className="text-purple-700 font-bold hover:underline"
              >
                Sign in
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => changeMode('login')}
              className="text-purple-700 font-bold hover:underline"
            >
              Back to sign in
            </button>
          )}
        </p>
      </div>
    </div>
  );
}
